import React from "react";
import classNames from "classnames";
import {
  ChildrenOptions,
  ConditionalLabelProps,
  ConditionalProps,
  FormFieldProps,
} from "./types";

const getConditionalProps = (
  group: FormFieldProps["group"],
  disabled: FormFieldProps["disabled"]
): ConditionalProps => {
  const props: ConditionalProps = {};

  if (group && disabled) {
    props.disabled = true;
  }

  return props;
};

const getConditionalLabelProps = (
  group: FormFieldProps["group"],
  id: FormFieldProps["id"]
) => {
  const props: ConditionalLabelProps = {};

  if (!group && id) {
    props["htmlFor"] = id;
  }

  return props;
};

const getOptions = (
  group: boolean,
  invalid: boolean,
  disabled: boolean,
  size: any,
  id: string,
  errors: string[] | React.ReactNode[] | undefined,
  helper: string | React.ReactNode | undefined
) => {
  let options: ChildrenOptions = {
    size,
    disabled,
  };

  if (!group) options.id = id;
  let ariaDescribedby = "";

  if (!group && invalid) options.invalid = invalid;
  if (helper) ariaDescribedby += `helper-form-field-${id} `;
  if (errors) ariaDescribedby += `error-form-field-${id}`;
  if (ariaDescribedby.length > 0) options["aria-describedby"] = ariaDescribedby;

  return options;
};

const FormField = ({
  children,
  className,
  disabled = false,
  errors,
  group = false,
  helper,
  id,
  invalid = false,
  isOnDarkCanvas = false,
  label,
  length,
  note,
  size = "md",
  ...rest
}: FormFieldProps) => {
  const uuid = id || `${Math.random().toString(36).slice(2, 16)}`;
  const options = getOptions(
    group,
    invalid,
    disabled,
    size,
    uuid,
    errors,
    helper
  );

  const formFieldClasses = classNames(
    "rds-FormField",
    {
      [`rds-FormField--${size}`]: size !== "md",
      "is-disabled": disabled,
      "is-invalid": invalid,
      "on-darkCanvas": isOnDarkCanvas,
    },
    className
  );

  const formFieldControlClasses = classNames("rds-FormField-control", {
    [`rds-FormField-control--${length}`]: length,
  });

  const Element = group ? "fieldset" : "div";
  const LabelElement = group ? "legend" : "label";

  const showErrors = invalid && errors && !disabled;

  return (
    <Element
      {...rest}
      {...getConditionalProps(group, disabled)}
      className={formFieldClasses}
    >
      {label && (
        <LabelElement
          className="rds-FormField-label"
          {...getConditionalLabelProps(group, uuid)}
        >
          {label}
          {note && <span className="rds-FormField-note">{note}</span>}
        </LabelElement>
      )}
      <div className={formFieldControlClasses}>{children(options)}</div>
      {helper && (
        <p id={`helper-form-field-${uuid}`} className="rds-FormField-helper">
          {helper}
        </p>
      )}
      {showErrors && (
        <div id={`error-form-field-${uuid}`}>
          {errors.map((error, i) => (
            <p key={i} className="rds-FormField-error">
              {error}
            </p>
          ))}
        </div>
      )}
    </Element>
  );
};

FormField.displayName = "FormField";

export default FormField;
